import { Language } from '../types/kundali';
import { formatOrdinal } from './dateUtils';

export interface LagnaInsight {
  archetype: string;
  nature: string;
  strengths: string[];
  advice: string;
  luckyColor: string;
  luckyDay: string;
  deity: string;
  mantra: string;
}

export const LAGNA_INSIGHTS: Record<string, { en: LagnaInsight; hi: LagnaInsight }> = {
  Aries: {
    en: {
      archetype: 'The Dynamic Pioneer & Trailblazer',
      nature: 'Energetic, courageous, direct, and action-oriented. You possess natural leadership drive and dislike delays.',
      strengths: ['High initiative & courage', 'Quick decision maker', 'Passionate & straightforward'],
      advice: 'Cultivate patience and avoid impulsive reactions in personal conversations.',
      luckyColor: 'Red, Coral, Warm Amber',
      luckyDay: 'Tuesday (मंगलवार)',
      deity: 'Lord Hanuman & Lord Kartikeya',
      mantra: 'ॐ अं अंगारकाय नमः (Om Angarakaya Namah)'
    },
    hi: {
      archetype: 'साहसी एवं नेतृत्वकारी व्यक्तित्व (मेष लग्न)',
      nature: 'आप ऊर्जावान, निडर, स्पष्टवादी और त्वरित निर्णय लेने वाले स्वभाव के हैं। पहल करने में सदैव आगे रहते हैं।',
      strengths: ['साहस एवं ऊर्जावान स्वभाव', 'तत्काल निर्णय लेने की क्षमता', 'स्पष्टवादिता और नेतृत्व क्षमता'],
      advice: 'जल्दबाजी और क्रोध पर नियंत्रण रखें, दूसरों की बात धैर्यपूर्वक सुनें।',
      luckyColor: 'लाल, नारंगी, मूंगा रंग',
      luckyDay: 'मंगलवार',
      deity: 'श्री हनुमान जी',
      mantra: 'ॐ हं हनुमते नमः'
    }
  },
  Taurus: {
    en: {
      archetype: 'The Grounded Builder & Sustainer',
      nature: 'Calm, patient, aesthetic, and persevering. You value stability, security, quality lifestyle, and enduring loyalties.',
      strengths: ['Reliable & steadfast', 'Appreciation for art & luxury', 'Practical financial sense'],
      advice: 'Embrace constructive changes and avoid resisting new approaches due to comfort zones.',
      luckyColor: 'White, Silver, Soft Pink, Turquoise',
      luckyDay: 'Friday (शुक्रवार)',
      deity: 'Goddess Lakshmi',
      mantra: 'ॐ शुं शुक्राय नमः (Om Shum Shukraya Namah)'
    },
    hi: {
      archetype: 'स्थिर, कलाप्रेमी एवं व्यावहारिक व्यक्तित्व (वृषभ लग्न)',
      nature: 'आप शांत, धैर्यवान, सौंदर्यप्रिय और जीवन में स्थायित्व व वित्तीय सुरक्षा को प्राथमिकता देने वाले हैं।',
      strengths: ['विश्वसनीयता एवं धैर्य', 'उत्कृष्ट वित्तीय समझ', 'सद्भाव और सुरुचिपूर्ण जीवनशैली'],
      advice: 'हठी स्वभाव से बचें और आवश्यकतानुसार नए विचारों को सहजता से स्वीकार करें।',
      luckyColor: 'सफेद, चमकीला गुलाबी, फिरोजी',
      luckyDay: 'शुक्रवार',
      deity: 'माता महालक्ष्मी',
      mantra: 'ॐ श्रीं महालक्ष्म्यै नमः'
    }
  },
  Gemini: {
    en: {
      archetype: 'The Curious Communicator & Thinker',
      nature: 'Intellectual, versatile, sociable, and quick-witted. You thrive on learning, networking, and exchanging fresh ideas.',
      strengths: ['Fluent communication', 'Mental agility & curiosity', 'Adaptable to changes'],
      advice: 'Avoid scattering your focus across too many projects simultaneously; finish what you start.',
      luckyColor: 'Emerald Green, Mint, Light Yellow',
      luckyDay: 'Wednesday (बुधवार)',
      deity: 'Lord Ganesha & Lord Vishnu',
      mantra: 'ॐ बुं बुधाय नमः (Om Bum Budhaya Namah)'
    },
    hi: {
      archetype: 'बुद्धिमान, जिज्ञासु एवं संवादकुशल व्यक्तित्व (मिथुन लग्न)',
      nature: 'आप बहुमुखी प्रतिभा, तीव्र बुद्धि, वाक्पटुता और नई जानकारी सीखने के प्रति अत्यधिक उत्सुक रहने वाले हैं।',
      strengths: ['उत्कृष्ट संवाद व लेखन कला', 'तीक्ष्ण बुद्धि व सीखने की लगन', 'त्वरित अनुकूलनशीलता'],
      advice: 'एक समय में बहुत अधिक कार्यों में उलझने से बचें, कार्यों को पूरा करने पर ध्यान दें।',
      luckyColor: 'हरा, तोतिया, हल्का पीला',
      luckyDay: 'बुधवार',
      deity: 'भगवान श्री गणेश जी',
      mantra: 'ॐ गं गणपतये नमः'
    }
  },
  Cancer: {
    en: {
      archetype: 'The Intuitive Caregiver & Protector',
      nature: 'Empathetic, deeply intuitive, protective, and family-centered. You feel energies deeply and care immensely for loved ones.',
      strengths: ['High emotional intelligence', 'Nurturing & supportive', 'Strong gut intuition'],
      advice: 'Guard against mood fluctuations and do not absorb others emotional stress into yourself.',
      luckyColor: 'Pearl White, Cream, Silver',
      luckyDay: 'Monday (सोमवार)',
      deity: 'Lord Shiva & Goddess Parvati',
      mantra: 'ॐ सों सोमाय नमः (Om Som Somaya Namah)'
    },
    hi: {
      archetype: 'संवेदनशील, सहृदय एवं रक्षक व्यक्तित्व (कर्क लग्न)',
      nature: 'आप अत्यधिक संवेदनशील, परिवार-प्रेमी, अंतर्ज्ञानी और दूसरों की भावनाओं को गहराई से समझने वाले हैं।',
      strengths: ['मजबूत भावनात्मक समझ', 'परिवार और मित्रों के प्रति असीम समर्पण', 'सटीक पूर्वाभास क्षमता'],
      advice: 'अत्यधिक भावुकता और मानसिक तनाव से बचें, नियमित ध्यान व प्राणायाम करें।',
      luckyColor: 'दूधिया सफेद, मोती रंग, चांदी',
      luckyDay: 'सोमवार',
      deity: 'भगवान शिव (महादेव)',
      mantra: 'ॐ नमः शिवाय'
    }
  },
  Leo: {
    en: {
      archetype: 'The Noble Leader & Sovereign',
      nature: 'Generous, confident, charismatic, and dignified. You naturally draw respect and lead organizations with grandeur.',
      strengths: ['Magnetic presence & leadership', 'Magnanimous generosity', 'Self-respect & ambition'],
      advice: 'Remain humble and receptive to constructive criticism from trusted peers.',
      luckyColor: 'Golden Yellow, Saffron, Orange',
      luckyDay: 'Sunday (रविवार)',
      deity: 'Lord Surya (Sun God) & Gayatri Mata',
      mantra: 'ॐ घृणि सूर्याय नमः (Om Ghrini Suryaya Namah)'
    },
    hi: {
      archetype: 'प्रतापी, स्वाभिमानी एवं नेतृत्वकारी व्यक्तित्व (सिंह लग्न)',
      nature: 'आप आत्मविश्वासी, उदार, प्रभावशाली और गरिमामयी व्यक्तित्व के धनी हैं। नेतृत्व करना आपका स्वाभाविक गुण है।',
      strengths: ['प्रभावशाली व्यक्तित्व व ओज', 'उदार हृदय एवं विश्वसनीयता', 'सम्मान एवं उच्च लक्ष्य'],
      advice: 'अहंकार और दूसरों पर अत्यधिक अधिकार जताने से बचें, सभी का परामर्श सुनें।',
      luckyColor: 'सुनहरा, केसरिया, गहरा पीला',
      luckyDay: 'रविवार',
      deity: 'भगवान सूर्य देव',
      mantra: 'ॐ सूर्याय नमः'
    }
  },
  Virgo: {
    en: {
      archetype: 'The Analytical Healer & Optimizer',
      nature: 'Methodical, detail-oriented, discerning, and dedicated to service. You excel at problem-solving and organizing chaos.',
      strengths: ['Sharp analytical precision', 'Organized & hardworking', 'Practical problem solving'],
      advice: 'Avoid over-analyzing minor flaws or being overly critical of yourself and others.',
      luckyColor: 'Leaf Green, Beige, Light Turquoise',
      luckyDay: 'Wednesday (बुधवार)',
      deity: 'Lord Vishnu & Ganesha',
      mantra: 'ॐ बुं बुधाय नमः (Om Bum Budhaya Namah)'
    },
    hi: {
      archetype: 'विश्लेषक, कर्तव्यनिष्ठ एवं विवेकशील व्यक्तित्व (कन्या लग्न)',
      nature: 'आप सूक्ष्मदृष्टि रखने वाले, सुव्यवस्थित, सेवाभावी और हर कार्य को अत्यंत बारीकी व निपुणता से करने वाले हैं।',
      strengths: ['बारीक विश्लेषण व तार्किक सोच', 'अनुशासन एवं कर्तव्यपरायणता', 'समस्या समाधान में दक्षता'],
      advice: 'छोटी-छोटी बातों की अत्यधिक चिंता और खुद पर अधिक आलोचना से बचें।',
      luckyColor: 'पन्ना हरा, हल्का हरा, बादामी',
      luckyDay: 'बुधवार',
      deity: 'भगवान विष्णु',
      mantra: 'ॐ नमो भगवते वासुदेवाय'
    }
  },
  Libra: {
    en: {
      archetype: 'The Harmonizer & Diplomat',
      nature: 'Charming, diplomatic, fair-minded, and aesthetic. You seek harmony, graceful partnerships, and justice in all affairs.',
      strengths: ['Diplomacy & peacemaking', 'Refined aesthetic taste', 'Balanced perspective'],
      advice: 'Overcome indecisiveness by trusting your inner core values rather than only seeking external balance.',
      luckyColor: 'Sky Blue, Pastel Pink, Cream, Diamond White',
      luckyDay: 'Friday (शुक्रवार)',
      deity: 'Goddess Saraswati & Lakshmi',
      mantra: 'ॐ शुं शुक्राय नमः (Om Shum Shukraya Namah)'
    },
    hi: {
      archetype: 'सद्भावप्रिय, न्यायप्रिय एवं कूटनीतिज्ञ व्यक्तित्व (तुला लग्न)',
      nature: 'आप आकर्षक, न्यायप्रिय, संतुलित सोच वाले और सुरुचिपूर्ण वातावरण व सुंदर संबंधों को चाहने वाले हैं।',
      strengths: ['कूटनीतिक एवं शांतिप्रिय व्यवहार', 'कला एवं सुरुचि की उत्तम समझ', 'निष्पक्ष निर्णय क्षमता'],
      advice: 'अनिर्णय की स्थिति से बचें, समय पर दृढ़ता से निर्णय लेना सीखें।',
      luckyColor: 'आसमानी, हल्का गुलाबी, क्रीम',
      luckyDay: 'शुक्रवार',
      deity: 'माता लक्ष्मी व सरस्वती',
      mantra: 'ॐ श्रीं नमः'
    }
  },
  Scorpio: {
    en: {
      archetype: 'The Resilient Alchemist & Transformer',
      nature: 'Intense, deeply perceptive, secretive, and resilient. You have immense willpower and rebound stronger from any challenge.',
      strengths: ['Unshakable willpower', 'Deep psychological perception', 'Fierce loyalty & courage'],
      advice: 'Practice forgiveness and release past grievances to unlock your highest spiritual power.',
      luckyColor: 'Deep Maroon, Rust, Blood Red',
      luckyDay: 'Tuesday (मंगलवार)',
      deity: 'Lord Shiva (Bhairava) & Hanuman',
      mantra: 'ॐ अं अंगारकाय नमः (Om Angarakaya Namah)'
    },
    hi: {
      archetype: 'गूढ़, दृढ़ निश्चयी एवं परिवर्तनकारी व्यक्तित्व (वृश्चिक लग्न)',
      nature: 'आप अत्यधिक दृढ़ संकल्प, गहरी समझ, रहस्यमयी और किसी भी परिस्थिति से जीतकर पुनः उभरने की शक्ति रखते हैं।',
      strengths: ['अडिग इच्छाशक्ति व साहस', 'गहन अंतर्दृष्टि व विश्लेषणात्मक शक्ति', 'सच्ची निष्ठा व विश्वसनीयता'],
      advice: 'पुरानी बातों और मनमुटाव को मन में बांधकर न रखें, क्षमा का अभ्यास करें।',
      luckyColor: 'गहरा लाल, मैरून, सिंदूरी',
      luckyDay: 'मंगलवार',
      deity: 'श्री हनुमान जी एवं काल भैरव',
      mantra: 'ॐ क्रां क्रीं क्रौं सः भौमाय नमः'
    }
  },
  Sagittarius: {
    en: {
      archetype: 'The Philosophical Seeker & Visionary',
      nature: 'Optimistic, philosophical, righteous, and visionary. You love truth, higher learning, adventure, and expansive horizons.',
      strengths: ['Natural optimism & wisdom', 'Honesty & ethical principles', 'Expansive visionary outlook'],
      advice: 'Exercise tact in communication; raw bluntness can sometimes inadvertently hurt sensitive feelings.',
      luckyColor: 'Saffron, Bright Gold, Yellow',
      luckyDay: 'Thursday (गुरुवार)',
      deity: 'Lord Dakshinamurthy & Brihaspati',
      mantra: 'ॐ बृं बृहस्पतये नमः (Om Brim Brihaspataye Namah)'
    },
    hi: {
      archetype: 'ज्ञानपिपासु, धर्मपरायण एवं दूरदर्शी व्यक्तित्व (धनु लग्न)',
      nature: 'आप आशावादी, दार्शनिक, सत्यवादी और उच्च ज्ञान व आध्यात्मिक लक्ष्यों की ओर अग्रसर रहने वाले हैं।',
      strengths: ['सकारात्मक सोच एवं स्वाभाविक ज्ञान', 'नैतिकता एवं न्यायप्रियता', 'दूरगामी सोच एवं उत्साह'],
      advice: 'अत्यधिक स्पष्टवादिता में कोमलता का ध्यान रखें ताकि दूसरों की भावनाएं आहत न हों।',
      luckyColor: 'पीला, केसरिया, सुनहरा',
      luckyDay: 'गुरुवार',
      deity: 'भगवान श्री हरि विष्णु एवं देवगुरु बृहस्पति',
      mantra: 'ॐ ग्रां ग्रीं ग्रौं सः गुरवे नमः'
    }
  },
  Capricorn: {
    en: {
      archetype: 'The Master Strategist & Architect',
      nature: 'Disciplined, pragmatic, ambitious, and enduring. You build enduring success through patience, structure, and hard work.',
      strengths: ['Unmatched persistence & discipline', 'Pragmatic long-term planning', 'Integrity & accountability'],
      advice: 'Take time to celebrate milestones and allow warmth into your daily routines rather than just all work.',
      luckyColor: 'Navy Blue, Charcoal, Deep Brown',
      luckyDay: 'Saturday (शनिवार)',
      deity: 'Lord Shiva & Lord Shani',
      mantra: 'ॐ शं शनैश्चराय नमः (Om Sham Shanaishcharaya Namah)'
    },
    hi: {
      archetype: 'अनुशासित, कर्मठ एवं रणनीतिकार व्यक्तित्व (मकर लग्न)',
      nature: 'आप अत्यंत परिश्रमी, व्यावहारिक, धैर्यवान और निरंतर प्रयास से अपने लक्ष्यों को प्राप्त करने वाले हैं।',
      strengths: ['कठिन परिश्रम एवं अनुशासन', 'दीर्घकालिक योजना एवं स्थायित्व', 'जिम्मेदारी व विश्वसनीयता'],
      advice: 'अत्यधिक कार्यभार से मानसिक तनाव न लें, परिवार व विश्राम को भी समय दें।',
      luckyColor: 'नीला, गहरा स्लेटी, काला',
      luckyDay: 'शनिवार',
      deity: 'भगवान शनिदेव एवं शिव जी',
      mantra: 'ॐ प्रां प्रीं प्रौं सः शनैश्चराय नमः'
    }
  },
  Aquarius: {
    en: {
      archetype: 'The Humanitarian Innovator & Free Spirit',
      nature: 'Forward-thinking, humanitarian, original, and independent. You think ahead of your time and value progressive concepts.',
      strengths: ['Visionary innovative thinking', 'Egalitarian & socially minded', 'Unconventional creativity'],
      advice: 'Connect emotionally with close individuals rather than viewing relationships purely conceptually.',
      luckyColor: 'Electric Blue, Violet, Silver',
      luckyDay: 'Saturday (शनिवार)',
      deity: 'Lord Shiva & Hanuman',
      mantra: 'ॐ शं शनैश्चराय नमः (Om Sham Shanaishcharaya Namah)'
    },
    hi: {
      archetype: 'दूरदर्शी, नवप्रवर्तनकारी एवं समाजहितैषी व्यक्तित्व (कुंभ लग्न)',
      nature: 'आप प्रगतिशील, मौलिक सोच वाले, स्वतंत्र विचारों के धनी और व्यापक समाज के कल्याण की सोचने वाले हैं।',
      strengths: ['अद्भुत नई सोच एवं रचनात्मकता', 'समानता व निष्पक्षता का दृष्टिकोण', 'अनुसंधान एवं तकनीकी समझ'],
      advice: 'अपनों के साथ भावनात्मक रूप से जुड़ें और मन की बातें साझा करें।',
      luckyColor: 'आसमानी नीला, बैंगनी, सलेटी',
      luckyDay: 'शनिवार',
      deity: 'भगवान शिव एवं श्री हनुमान जी',
      mantra: 'ॐ शं शनये नमः'
    }
  },
  Pisces: {
    en: {
      archetype: 'The Compassionate Mystic & Visionary',
      nature: 'Imaginative, compassionate, spiritual, and artistic. You possess profound empathy and deep philosophical intuition.',
      strengths: ['Deep empathy & kindness', 'Vivid artistic imagination', 'Spiritual wisdom & intuition'],
      advice: 'Maintain clear practical boundaries and ground your creative visions in structured daily habits.',
      luckyColor: 'Turquoise, Light Yellow, Pearl White',
      luckyDay: 'Thursday (गुरुवार)',
      deity: 'Lord Vishnu & Shiva',
      mantra: 'ॐ बृं बृहस्पतये नमः (Om Brim Brihaspataye Namah)'
    },
    hi: {
      archetype: 'करुणामयी, आध्यात्मिक एवं कलात्मक व्यक्तित्व (मीन लग्न)',
      nature: 'आप अत्यंत दयालु, कल्पनाशील, अंतर्दृष्टि सम्पन्न और आध्यात्मिक चेतना से ओत-प्रोत व्यक्तित्व के हैं।',
      strengths: ['गहरी संवेदनशीलता एवं परोपकार', 'सृजनात्मक कल्पना एवं कलात्मक रुचि', 'आध्यात्मिक रुझान'],
      advice: 'व्यावहारिक सीमाओं का ध्यान रखें और अपने विचारों को धरातल पर क्रियान्वित करें।',
      luckyColor: 'हल्का पीला, सुनहरी केसरिया, समुद्री हरा',
      luckyDay: 'गुरुवार',
      deity: 'भगवान विष्णु (नारायण)',
      mantra: 'ॐ नमो नारायणाय'
    }
  }
};

export interface HousePlainMeaning {
  title: string;
  desc: string;
  focus: string;
}

export interface DetailedHouseData {
  houseNumber: number;
  sanskritName: string;
  sanskritNameHi: string;
  titleEn: string;
  titleHi: string;
  categoryEn: string; // e.g. 'Kendra (Pillar of Life)', 'Trikona (Dharma & Grace)', 'Upachaya (House of Growth)', 'Dusthana (Trik Bhava)'
  categoryHi: string;
  purusharthaEn: string; // 'Dharma' | 'Artha' | 'Kama' | 'Moksha'
  purusharthaHi: string;
  karakasEn: string[];
  karakasHi: string[];
  coreSignificationsEn: string[];
  coreSignificationsHi: string[];
  governingThemesEn: string;
  governingThemesHi: string;
}

export const DETAILED_HOUSE_TAXONOMY: Record<number, DetailedHouseData> = {
  1: {
    houseNumber: 1,
    sanskritName: 'Tanu Bhava (तनु भाव)',
    sanskritNameHi: 'तनु भाव (लग्न)',
    titleEn: '1st House (Lagna - Self, Body & Life Force)',
    titleHi: 'प्रथम भाव (लग्न - व्यक्तित्व, देह व आत्मबल)',
    categoryEn: 'Kendra & Trikona (Supreme Foundation / Vishnu-Lakshmi Sthana)',
    categoryHi: 'केंद्र एवं त्रिकोण (जीवन का परम आधार / विष्णु-लक्ष्मी स्थान)',
    purusharthaEn: 'Dharma (Purpose, Identity & Righteous Conduct)',
    purusharthaHi: 'धर्म (आत्म-स्वरूप, कर्तव्य व चारित्रिक तेज)',
    karakasEn: ['Sun (Soul & Vitality)', 'Mars (Physical Stamina)'],
    karakasHi: ['सूर्य (आत्मबल व तेज)', 'मंगल (शारीरिक ऊर्जा)'],
    coreSignificationsEn: ['Physical health & constitution', 'Natural temperament & demeanor', 'Fame, longevity & vital force', 'Primary outlook on worldly life'],
    coreSignificationsHi: ['शारीरिक स्वास्थ्य व ऊर्जा', 'स्वाभाविक स्वभाव व आचरण', 'दीर्घायु, सम्मान व आत्म-पहचान', 'जीवन के प्रति समग्र दृष्टिकोण'],
    governingThemesEn: 'The First House is the supreme pillar of your horoscope. It governs your vitality, executive presence, physical vitality, and overall life trajectory.',
    governingThemesHi: 'प्रथम भाव आपकी जन्म पत्रिका का प्राण है। यह आपके शारीरिक स्वास्थ्य, आत्मविश्वास, जीवन के मूल उद्देश्य और आत्मबल का निर्धारण करता है।'
  },
  2: {
    houseNumber: 2,
    sanskritName: 'Dhana & Kutumba Bhava (धन व कुटुंब भाव)',
    sanskritNameHi: 'धन व कुटुंब भाव',
    titleEn: '2nd House (Dhana - Wealth, Speech & Family Lineage)',
    titleHi: 'द्वितीय भाव (धन, वाणी एवं कुल परंपरा)',
    categoryEn: 'Panapara & Maraka (Sustenance & Resource Sthana)',
    categoryHi: 'पणफर एवं मारक (पोषण व संचित संपदा स्थान)',
    purusharthaEn: 'Artha (Financial Security & Tangible Assets)',
    purusharthaHi: 'अर्थ (संचित संपदा, वित्तीय सुरक्षा व पोषण)',
    karakasEn: ['Jupiter (Wealth & Wisdom)', 'Mercury (Eloquence & Speech)'],
    karakasHi: ['गुरु (धन व संस्कार)', 'बुध (वाणी व वाकपटुता)'],
    coreSignificationsEn: ['Accumulated savings & liquid assets', 'Voice tone, truthfulness & communication style', 'Family lineage, values & upbringing', 'Nutritional habits and right eye'],
    coreSignificationsHi: ['संचित बैंक बैलेंस व स्वर्ण संपदा', 'वाणी का प्रभाव, मधुरता व सत्यवादिता', 'पारिवारिक संस्कार व कुल परंपरा', 'खानपान की आदतें व मुख सौंदर्य'],
    governingThemesEn: 'The Second House governs accumulated liquid wealth, family support systems, oral expression, and food habits. Strong placements here grant financial prudence and persuasive speech.',
    governingThemesHi: 'द्वितीय भाव आपके संचित धन, बैंक कोष, पारिवारिक पृष्ठभूमि तथा प्रभावशाली वाणी का प्रतिनिधित्व करता है। इसमें शुभ स्थिति दीर्घकालिक आर्थिक सुरक्षा प्रदान करती है।'
  },
  3: {
    houseNumber: 3,
    sanskritName: 'Sahaja & Parakrama Bhava (सहज व पराक्रम भाव)',
    sanskritNameHi: 'सहज व पराक्रम भाव',
    titleEn: '3rd House (Sahaja - Courage, Initiative & Digital Skills)',
    titleHi: 'तृतीय भाव (पराक्रम, उद्यम व संचार कौशल)',
    categoryEn: 'Upachaya (House of Progressive Compounding Growth)',
    categoryHi: 'उपचय (प्रयास से निरंतर वृद्धि देने वाला भाव)',
    purusharthaEn: 'Kama (Drive, Passions & Personal Initiative)',
    purusharthaHi: 'काम (इच्छाएं, उद्यम व साहसिक पहल)',
    karakasEn: ['Mars (Bravery & Sibling bond)', 'Mercury (Writing & Media)'],
    karakasHi: ['मंगल (साहस व छोटे भाई-बहन)', 'बुध (लेखन व संचार)'],
    coreSignificationsEn: ['Self-made efforts & enterprise', 'Younger siblings and close allies', 'Writing, media, and digital publishing skills', 'Short travels and manual dexterity'],
    coreSignificationsHi: ['स्व-प्रयास, उद्यमशीलता व पराक्रम', 'छोटे भाई-बहनों का सहयोग', 'लेखन, मीडिया व डिजिटल संवाद कौशल', 'लघु यात्राएं व व्यावहारिक दक्षता'],
    governingThemesEn: 'The Third House is an Upachaya bhava where results steadily compound over time through self-effort, courage, communication, and creative projects.',
    governingThemesHi: 'तृतीय भाव आपकी कर्मठता, साहस, छोटे भाई-बहनों और मीडिया-संवाद कौशल का केंद्र है। इसमें निरंतर परिश्रम से उत्तरोत्तर उन्नति प्राप्त होती है।'
  },
  4: {
    houseNumber: 4,
    sanskritName: 'Sukha & Matru Bhava (सुख व मातृ भाव)',
    sanskritNameHi: 'सुख व मातृ भाव (चतुर्थ)',
    titleEn: '4th House (Sukha - Home, Mother, Property & Inner Peace)',
    titleHi: 'चतुर्थ भाव (सुख, माता, गृह व वाहन संपदा)',
    categoryEn: 'Kendra (Foundational Pillar / Heart of the Chart)',
    categoryHi: 'केंद्र (हृदय स्थान / सुख व मानसिक शांति का आधार)',
    purusharthaEn: 'Moksha (Emotional Sanctuary & Inner Fulfillment)',
    purusharthaHi: 'मोक्ष (मानसिक तृप्ति व अंतःकरण की शांति)',
    karakasEn: ['Moon (Mother & Emotions)', 'Venus (Vehicles & Luxury Property)'],
    karakasHi: ['चन्द्र (माता व मानसिक शांति)', 'शुक्र (वाहन व गृह सौंदर्य)'],
    coreSignificationsEn: ['Mother’s affection & maternal roots', 'Real estate, homes, and land ownership', 'Vehicles, physical comforts & luxuries', 'Subconscious emotional tranquility'],
    coreSignificationsHi: ['माता का वात्सल्य व उनका स्वास्थ्य', 'भूमि, भवन, अचल संपत्ति व घर का वातावरण', 'वाहन सुख व जीवन की सुख-सुविधाएं', 'हृदय की शांति व आंतरिक संतोष'],
    governingThemesEn: 'The Fourth House represents your inner emotional peace, relationship with your mother, real estate holdings, and domestic happiness.',
    governingThemesHi: 'चतुर्थ भाव मानसिक शांति, मातृ-स्नेह, गृह-सुख एवं अचल संपत्ति (मकान, भूमि, वाहन) का कारक है। यह आपके आंतरिक सुकून की स्थिति को दर्शाता है।'
  },
  5: {
    houseNumber: 5,
    sanskritName: 'Putra & Purva Punya Bhava (पुत्र व पूर्व पुण्य भाव)',
    sanskritNameHi: 'पुत्र व पूर्व पुण्य भाव (पंचम)',
    titleEn: '5th House (Putra - Intellect, Progeny, Creativity & Wisdom)',
    titleHi: 'पंचम भाव (बुद्धि, संतान, पूर्व पुण्य व विद्या)',
    categoryEn: 'Trikona (Most Auspicious Lakshmi Sthana of Divine Merit)',
    categoryHi: 'त्रिकोण (अत्यंत शुभ लक्ष्मी स्थान / पूर्व जन्म के पुण्य)',
    purusharthaEn: 'Dharma (Higher Intellect, Right Decision-Making & Ethics)',
    purusharthaHi: 'धर्म (सद्बुद्धि, विवेक व सन्मार्ग)',
    karakasEn: ['Jupiter (Children & Higher Wisdom)', 'Mercury (Analytical Discernment)'],
    karakasHi: ['गुरु (संतान, ज्ञान व देव कृपा)', 'बुध (तीक्ष्ण बुद्धि)'],
    coreSignificationsEn: ['Creative intelligence & problem solving', 'Children, parenting & family lineage continuity', 'Past-life good deeds (Purva Punya)', 'Mantra siddhi, spiritual intuition & investments'],
    coreSignificationsHi: ['रचनात्मक बुद्धि व रणनीतिक निर्णय', 'संतान सुख, विद्या व उच्च अध्ययन', 'पूर्व जन्म के संचित पुण्य व ईश्वरीय कृपा', 'मंत्र साधना, अंतर्ज्ञान व वित्तीय निवेश'],
    governingThemesEn: 'The Fifth House is a divine Trikona house governing speculative intellect, educational excellence, parenting joy, and moral discernment.',
    governingThemesHi: 'पंचम भाव पूर्वजन्म के शुभ कर्मों, विद्या, बुद्धि, संतान एवं आध्यात्मिक विवेक का भाव है। यह जीवन में स्वतः मिलने वाले लाभ व ज्ञान को प्रकट करता है।'
  },
  6: {
    houseNumber: 6,
    sanskritName: 'Ripu & Roga Bhava (रिपु, रोग व ऋण भाव)',
    sanskritNameHi: 'रिपु व ऋण भाव (षष्ठ)',
    titleEn: '6th House (Ripu - Service, Overcoming Obstacles & Health Discipline)',
    titleHi: 'षष्ठ भाव (शत्रु विजय, रोग प्रतिरोध व सेवा कार्य)',
    categoryEn: 'Upachaya & Dusthana (House of Resilience & Victorious Effort)',
    categoryHi: 'उपचय एवं त्रिक (संघर्षों पर विजय व कार्यकुशलता)',
    purusharthaEn: 'Artha (Daily Routine, Employment & Debt Resolution)',
    purusharthaHi: 'अर्थ (दैनिक कार्य, सेवाभाव व ऋण मुक्ति)',
    karakasEn: ['Mars (Competitive Victory)', 'Saturn (Discipline & Duty)'],
    karakasHi: ['मंगल (प्रतियोगिता व विजय)', 'शनि (कठोर परिश्रम व अनुशासन)'],
    coreSignificationsEn: ['Overcoming competitors and legal hurdles', 'Daily work ethic and employment responsibilities', 'Immune resilience and lifestyle health management', 'Debt management and problem resolution'],
    coreSignificationsHi: ['प्रतिद्वंद्वियों पर विजय व विवाद समाधान', 'दैनिक नौकरी, सेवा व कर्तव्यपरायणता', 'रोग प्रतिरोधक क्षमता व स्वास्थ्य अनुशासन', 'ऋण प्रबंधन एवं जटिल समस्याओं का निवारण'],
    governingThemesEn: 'The Sixth House represents the grit to overcome worldly struggles. Benefic placements or strong lords here grant victory in competitive exams, litigation, and career hurdles.',
    governingThemesHi: 'षष्ठ भाव जीवन की चुनौतियों, नौकरी, सेवा और स्वास्थ्य का भाव है। यह विपरीत परिस्थितियों से लड़कर विजय प्राप्त करने की आंतरिक क्षमता प्रदान करता है।'
  },
  7: {
    houseNumber: 7,
    sanskritName: 'Kalatra & Jaya Bhava (कलत्र व जाया भाव)',
    sanskritNameHi: 'कलत्र भाव (सप्तम)',
    titleEn: '7th House (Kalatra - Marriage, Partnership & Public Deals)',
    titleHi: 'सप्तम भाव (विवाह, जीवनसाथी व व्यावसायिक साझेदारी)',
    categoryEn: 'Kendra & Maraka (Union & Public Interaction Sthana)',
    categoryHi: 'केंद्र एवं मारक (दाम्पत्य सुख व लोक व्यवहार का केंद्र)',
    purusharthaEn: 'Kama (Partnerships, Harmony & Shared Aspirations)',
    purusharthaHi: 'काम (पारस्परिक सामंजस्य, प्रेम व संबंध)',
    karakasEn: ['Venus (Marital Bliss & Diplomacy)', 'Jupiter (Ethical Partner)'],
    karakasHi: ['शुक्र (दाम्पत्य सुख व आकर्षण)', 'गुरु (धार्मिक व निष्ठावान साथी)'],
    coreSignificationsEn: ['Spouse nature, marital bonding & harmony', 'Business partnerships and professional joint ventures', 'Public reputation and diplomatic negotiations', 'Overseas dealings and daily trade'],
    coreSignificationsHi: ['जीवनसाथी का स्वभाव, रूप व वैवाहिक सुख', 'व्यापारिक साझीदारियां व व्यावसायिक समझौते', 'सामाजिक मान-प्रतिष्ठा व जनसंपर्क', 'व्यापार एवं सार्वजनिक संबंध'],
    governingThemesEn: 'The Seventh House sits directly opposite the Lagna, representing your reflection in the mirror of relationships, marital harmony, and business alliances.',
    governingThemesHi: 'सप्तम भाव आपके जीवनसाथी, वैवाहिक जीवन की मधुरता तथा व्यापारिक साझेदारियों का मुख्य भाव है। यह सामाजिक संपर्कों की गुणवत्ता तय करता है।'
  },
  8: {
    houseNumber: 8,
    sanskritName: 'Randhra & Ayu Bhava (रंध्र व आयु भाव)',
    sanskritNameHi: 'आयु व गुप्त ज्ञान भाव (अष्टम)',
    titleEn: '8th House (Randhra - Longevity, Deep Research & Transformation)',
    titleHi: 'अष्टम भाव (दीर्घायु, शोध, पैतृक संपत्ति व रूपांतरण)',
    categoryEn: 'Dusthana (House of Profound Metamorphosis & Hidden Truths)',
    categoryHi: 'त्रिक भाव (गूढ़ विद्या, जीवन रूपांतरण व आंतरिक परिवर्तन)',
    purusharthaEn: 'Moksha (Ego Transcendence & Kundalini Awakening)',
    purusharthaHi: 'मोक्ष (अहंकार मुक्ति व गूढ़ आध्यात्मिक जागृति)',
    karakasEn: ['Saturn (Longevity & Endurance)'],
    karakasHi: ['शनि (आयु, धैर्य व सहनशीलता)'],
    coreSignificationsEn: ['Longevity and physical endurance', 'Inherited wealth, insurance, and sudden windfalls', 'Deep occult research, astrology, and psychology', 'Profound spiritual metamorphosis'],
    coreSignificationsHi: ['दीर्घायु, जीवन रक्षा व सहनशक्ति', 'पैतृक संपत्ति, वसीयत व अप्रत्याशित वित्तीय लाभ', 'गूढ़ ज्ञान, ज्योतिष, शोध व अनुसंधान', 'आध्यात्मिक साधना एवं आंतरिक रूपांतरण'],
    governingThemesEn: 'The Eighth House is the realm of profound mysteries, hidden treasures, longevity, and psychological transformations that reshape your life for higher purpose.',
    governingThemesHi: 'अष्टम भाव आयु, गुप्त ज्ञान, शोध, वसीयत और जीवन के बड़े परिवर्तनों का भाव है। यह व्यक्ति को गहरी अंतर्दृष्टि और आध्यात्मिक परिपक्वता प्रदान करता है।'
  },
  9: {
    houseNumber: 9,
    sanskritName: 'Bhagya & Dharma Bhava (भाग्य व धर्म भाव)',
    sanskritNameHi: 'भाग्य व धर्म भाव (नवम)',
    titleEn: '9th House (Bhagya - Divine Grace, Higher Learning & Father)',
    titleHi: 'नवम भाव (भाग्य, धर्म, गुरु कृपा व पिता का सहयोग)',
    categoryEn: 'Trikona (Supreme Lakshmi Sthana of Fortune & Ethics)',
    categoryHi: 'त्रिकोण (सर्वश्रेष्ठ लक्ष्मी स्थान / परम भाग्योदय भाव)',
    purusharthaEn: 'Dharma (Cosmic Alignment, Right Action & Wisdom)',
    purusharthaHi: 'धर्म (सत्कर्म, ईश्वरीय कृपा व मार्गदर्शन)',
    karakasEn: ['Jupiter (Guru, Dharma & Divine Grace)', 'Sun (Father & Divine Law)'],
    karakasHi: ['गुरु (गुरु कृपा, ज्ञान व धर्म)', 'सूर्य (पिता व सत्कर्म)'],
    coreSignificationsEn: ['Divine fortune, auspicious opportunities & luck', 'Higher university education and philosophical wisdom', 'Paternal support and guru guidance', 'Long-distance pilgrimages and charitable dharma'],
    coreSignificationsHi: ['ईश्वरीय कृपा, भाग्योदय व अप्रत्याशित अवसर', 'उच्च शिक्षा, दर्शनशास्त्र व ज्ञानार्जन', 'पिता का आशीर्वाद व गुरुजनों का मार्गदर्शन', 'तीर्थयात्राएं, दान-पुण्य व धार्मिक निष्ठा'],
    governingThemesEn: 'The Ninth House is the most fortunate house in Vedic Astrology, acting as the protective umbrella of grace (Bhagya) that carries you safely through life obstacles.',
    governingThemesHi: 'नवम भाव पत्रिका का सबसे शुभ भाव है। यह भाग्य, गुरु, पिता, धर्म और ईश्वरीय अनुग्रह का प्रतीक है, जो हर कठिन मोड़ पर जातक को संबल प्रदान करता है।'
  },
  10: {
    houseNumber: 10,
    sanskritName: 'Karma & Rajyashrita Bhava (कर्म व राज्य भाव)',
    sanskritNameHi: 'कर्म व प्रतिष्ठा भाव (दशम)',
    titleEn: '10th House (Karma - Career, Executive Authority & Public Fame)',
    titleHi: 'दशम भाव (करियर, मान-सम्मान, पद-प्रतिष्ठा व शासन)',
    categoryEn: 'Kendra & Upachaya (Highest Sthana of Secular Power)',
    categoryHi: 'केंद्र एवं उपचय (सर्वोच्च कर्म क्षेत्र व सामाजिक प्रतिष्ठा)',
    purusharthaEn: 'Artha (Professional Status, Authority & Societal Impact)',
    purusharthaHi: 'अर्थ (सामाजिक प्रतिष्ठा, प्रशासनिक अधिकार व ख्याति)',
    karakasEn: ['Sun (Leadership & Fame)', 'Mercury (Commerce)', 'Jupiter (Advisory)', 'Saturn (Labor & Persistence)'],
    karakasHi: ['सूर्य (नेतृत्व व मान-सम्मान)', 'बुध (व्यापार)', 'गुरु (परामर्श व नीति)', 'शनि (परिश्रम व स्थायित्व)'],
    coreSignificationsEn: ['Career advancement, executive status and rank', 'Public reputation and professional legacy', 'Government ties, royal favor, and societal recognition', 'Sense of worldly duty and accomplishment'],
    coreSignificationsHi: ['करियर में पदोन्नति, प्रशासनिक दायित्व व उच्च पद', 'समाज में प्रतिष्ठा, मान-सम्मान व प्रभाव', 'शासन-प्रशासन से लाभ व राजकीय सम्मान', 'कर्मनिष्ठा एवं व्यावसायिक सफलता'],
    governingThemesEn: 'The Tenth House is the midheaven (zenith) of your birth chart, governing your worldly achievements, professional authority, and public accomplishments.',
    governingThemesHi: 'दशम भाव आपके कर्मक्षेत्र, पद-प्रतिष्ठा, व्यवसाय और समाज में मान-सम्मान का सर्वोच्च केंद्र है। यह व्यक्ति की कार्यकुशलता और नेतृत्व क्षमता को उजागर करता है।'
  },
  11: {
    houseNumber: 11,
    sanskritName: 'Labha & Aaya Bhava (लाभ व आय भाव)',
    sanskritNameHi: 'लाभ व सिद्धि भाव (एकादश)',
    titleEn: '11th House (Labha - Financial Inflows, Aspirations & Network)',
    titleHi: 'एकादश भाव (आय, लाभ, सिद्धि व मित्र मंडल)',
    categoryEn: 'Upachaya (House of Compounding Financial Prosperity)',
    categoryHi: 'उपचय (समस्त मनोकामनाओं की पूर्ति व निरंतर लाभ)',
    purusharthaEn: 'Kama (Realization of High Hopes, Dreams & Ambitions)',
    purusharthaHi: 'काम (महत्वाकांक्षाओं की पूर्ति व आर्थिक समृद्धि)',
    karakasEn: ['Jupiter (Expansion of Wealth & Beneficial Allies)'],
    karakasHi: ['गुरु (धन वृद्धि, समृद्धि व शुभ मार्गदर्शक)'],
    coreSignificationsEn: ['Regular recurring income and business profits', 'Fulfillment of long-cherished ambitions', 'Influential networks, elite circles, and mentors', 'Elder siblings and cooperative associations'],
    coreSignificationsHi: ['नियमित आय, व्यापारिक मुनाफा व वित्तीय लाभ', 'महत्वाकांक्षाओं एवं दीर्घकालिक सपनों की सिद्धि', 'प्रतिष्ठित मित्र-मंडल, सलाहकार व नेटवर्क', 'बड़े भाई-बहनों का सहयोग व सामाजिक प्रतिष्ठा'],
    governingThemesEn: 'The Eleventh House is the house of all gains (Sarva Labha). It ensures that your hard work in the 10th house translates into measurable financial prosperity and social influence.',
    governingThemesHi: 'एकादश भाव सर्व-मनोकामना पूर्ति और प्रचुर आर्थिक लाभ का भाव है। यह दशम भाव के कर्मों को वास्तविक धन और सामाजिक प्रभाव में रूपांतरित करता है।'
  },
  12: {
    houseNumber: 12,
    sanskritName: 'Vyaya & Moksha Bhava (व्यय व मोक्ष भाव)',
    sanskritNameHi: 'व्यय व मोक्ष भाव (द्वादश)',
    titleEn: '12th House (Vyaya - Foreign Horizons, Solitude & Moksha)',
    titleHi: 'द्वादश भाव (विदेश यात्रा, व्यय संतुलन, साधना व मोक्ष)',
    categoryEn: 'Dusthana & Moksha (House of Release, Rest & Liberation)',
    categoryHi: 'त्रिक एवं मोक्ष (आध्यात्मिक मुक्ति, निद्रा व वैराग्य)',
    purusharthaEn: 'Moksha (Ultimate Spiritual Realization & Detachment)',
    purusharthaHi: 'मोक्ष (आध्यात्मिक मुक्ति, ध्यान व वैराग्य)',
    karakasEn: ['Saturn (Detachment)', 'Ketu (Spiritual Liberation)', 'Venus (Bed Pleasures & Comfort)'],
    karakasHi: ['शनि (वैराग्य)', 'केतु (कैवल्य व मोक्ष)', 'शुक्र (शयन सुख व विलासिता)'],
    coreSignificationsEn: ['Foreign lands, international trade & overseas travel', 'Spiritual retreats, meditation, and quiet reflection', 'Balanced expenditures and philanthropic investments', 'Sound sleep quality and psychological release'],
    coreSignificationsHi: ['विदेश यात्रा, सुदूर देशों में कार्य व अंतरराष्ट्रीय संबंध', 'ध्यान, योग-साधना, एकांत चिंतन व मोक्ष', 'व्यय संतुलन, परोपकार व आध्यात्मिक निवेश', 'गहरी निद्रा, विश्राम एवं मानसिक तनावमुक्ति'],
    governingThemesEn: 'The Twelfth House completes the cosmic zodiac cycle, governing foreign connections, expenditure discipline, deep meditation, and ultimate spiritual liberation.',
    governingThemesHi: 'द्वादश भाव चक्र की पूर्णता है। यह विदेश गमन, ध्यान-साधना, संतुलित व्यय और अंततः मोक्ष की ओर ले जाने वाली आध्यात्मिक चेतना का प्रतिनिधित्व करता है।'
  }
};

export const HOUSE_PLAIN_MEANINGS: Record<number, { en: HousePlainMeaning; hi: HousePlainMeaning }> = {
  1: {
    en: { title: '1st House (Lagna - Self & Vitality)', desc: 'Represents your core personality, physical vitality, temperament, and life approach.', focus: 'Self, Health, Temperament' },
    hi: { title: 'प्रथम भाव (लग्न - व्यक्तित्व व आत्मबल)', desc: 'आपके व्यक्तित्व, शारीरिक स्वास्थ्य, आत्मबल और जीवन दृष्टिकोण का मुख्य आधार।', focus: 'व्यक्तित्व, स्वास्थ्य, आत्मबल' }
  },
  2: {
    en: { title: '2nd House (Dhana - Wealth & Speech)', desc: 'Represents accumulated savings, voice clarity, family lineage, and material security.', focus: 'Savings, Family, Voice' },
    hi: { title: 'द्वितीय भाव (धन व वाणी)', desc: 'संचित धन, बैंक बैलेंस, वाणी का प्रभाव और पारिवारिक संस्कार का भाव।', focus: 'संचित धन, परिवार, वाणी' }
  },
  3: {
    en: { title: '3rd House (Sahaja - Courage & Skills)', desc: 'Represents personal initiative, sibling bonds, communication skills, and creative efforts.', focus: 'Courage, Skills, Siblings' },
    hi: { title: 'तृतीय भाव (पराक्रम व कौशल)', desc: 'साहस, उद्यम, छोटे भाई-बहन, लेखन व डिजिटल संचार कौशल का भाव।', focus: 'साहस, छोटे भाई-बहन, कला' }
  },
  4: {
    en: { title: '4th House (Sukha - Home & Happiness)', desc: 'Represents emotional peace, mother, residential comfort, land, and vehicles.', focus: 'Home, Mother, Vehicles' },
    hi: { title: 'चतुर्थ भाव (सुख व माता)', desc: 'माता का स्नेह, गृह सुख, वाहन, भूमि-भवन और मन की आंतरिक शांति।', focus: 'गृह सुख, माता, भूमि-वाहन' }
  },
  5: {
    en: { title: '5th House (Putra - Wisdom & Children)', desc: 'Represents creative intellect, children, past-life merits, and higher discernment.', focus: 'Intellect, Children, Creativity' },
    hi: { title: 'पंचम भाव (विद्या व संतान)', desc: 'बुद्धि, पूर्वजन्म के पुण्य, उच्च शिक्षा, रचनात्मकता और संतान सुख।', focus: 'बुद्धि, संतान, पूर्व पुण्य' }
  },
  6: {
    en: { title: '6th House (Ripu - Work & Overcoming Obstacles)', desc: 'Represents daily employment, problem-solving resilience, and health discipline.', focus: 'Overcoming Obstacles, Work' },
    hi: { title: 'षष्ठ भाव (शत्रु, रोग व सेवा)', desc: 'प्रतियोगिता में विजय, दैनिक नौकरी, ऋण प्रबंधन और स्वास्थ्य प्रतिरोधक क्षमता।', focus: 'नौकरी, रोग प्रतिरोध, विजय' }
  },
  7: {
    en: { title: '7th House (Kalatra - Marriage & Partnerships)', desc: 'Represents spouse qualities, marital harmony, business collaborations, and public dealings.', focus: 'Spouse, Marriage, Business' },
    hi: { title: 'सप्तम भाव (विवाह व साझेदारी)', desc: 'जीवनसाथी, वैवाहिक सामंजस्य, व्यापारिक साझेदारी और सामाजिक संबंध।', focus: 'जीवनसाथी, वैवाहिक सुख, व्यापार' }
  },
  8: {
    en: { title: '8th House (Randhra - Transformation & Longevity)', desc: 'Represents longevity, deep research, unearned gains, and inner spiritual renewal.', focus: 'Transformation, Longevity' },
    hi: { title: 'अष्टम भाव (आयु व अनुसंधान)', desc: 'आयु, गुप्त ज्ञान, शोध, पैतृक संपत्ति और जीवन के गहन परिवर्तन।', focus: 'आयु, गुप्त विद्या, रूपांतरण' }
  },
  9: {
    en: { title: '9th House (Bhagya - Fortune & Dharma)', desc: 'Represents divine grace, higher learning, father, ethics, and auspicious long journeys.', focus: 'Fortune (Bhagya), Dharma, Father' },
    hi: { title: 'नवम भाव (भाग्य व धर्म)', desc: 'ईश्वरीय कृपा, भाग्य, धर्म, पिता का सहयोग और लंबी शुभ यात्राएं।', focus: 'भाग्य, धर्म, पिता का आशीर्वाद' }
  },
  10: {
    en: { title: '10th House (Karma - Career & Authority)', desc: 'Represents professional status, leadership achievements, public fame, and life accomplishments.', focus: 'Career, Fame, Leadership' },
    hi: { title: 'दशम भाव (कर्म व मान-सम्मान)', desc: 'कार्यक्षेत्र, पद-प्रतिष्ठा, प्रशासनिक सफलता और समाज में मान-सम्मान।', focus: 'करियर, प्रतिष्ठा, प्रशासनिक सफलता' }
  },
  11: {
    en: { title: '11th House (Labha - Gains & Network)', desc: 'Represents regular income, fulfillment of aspirations, influential friendships, and asset gains.', focus: 'Gains, Income, Network' },
    hi: { title: 'एकादश भाव (आय व लाभ)', desc: 'नियमित आर्थिक लाभ, मनोकामनाओं की पूर्ति, बड़े भाई-बहन और मित्र मंडल।', focus: 'आर्थिक लाभ, मनोकामना पूर्ति' }
  },
  12: {
    en: { title: '12th House (Vyaya - Peace & Liberation)', desc: 'Represents foreign connections, expenditure balance, meditation, and spiritual liberation (Moksha).', focus: 'Foreign, Meditation, Peace' },
    hi: { title: 'द्वादश भाव (मोक्ष व विदेश)', desc: 'विदेश यात्रा, व्यय संतुलन, ध्यान-साधना और आध्यात्मिक शांति।', focus: 'विदेश संबंध, साधना, मोक्ष' }
  }
};

export function getAuspiciousDirection(lagnaSign: string, lang: Language): string {
  const isHi = lang === 'hi';
  const fireSigns = ['Aries', 'Leo', 'Sagittarius'];
  const earthSigns = ['Taurus', 'Virgo', 'Capricorn'];
  const airSigns = ['Gemini', 'Libra', 'Aquarius'];

  if (fireSigns.includes(lagnaSign)) return isHi ? 'पूर्व (East)' : 'East';
  if (earthSigns.includes(lagnaSign)) return isHi ? 'दक्षिण (South)' : 'South';
  if (airSigns.includes(lagnaSign)) return isHi ? 'पश्चिम (West)' : 'West';
  return isHi ? 'उत्तर (North)' : 'North';
}

export interface DetailedHouseAnalysisResult {
  houseNumber: number;
  taxonomy: DetailedHouseData;
  sign: string;
  signLord: string;
  signLordHouse: number;
  signLordSign: string;
  occupyingPlanets: Array<{
    name: string;
    dignity: string;
    isRetrograde: boolean;
    isCombust: boolean;
    nakshatra: string;
  }>;
  synthesisEn: string;
  synthesisHi: string;
  lordPlacementAnalysisEn: string;
  lordPlacementAnalysisHi: string;
  actionGuidanceEn: string;
  actionGuidanceHi: string;
}

export function getDetailedHouseSynthesis(
  houseNumber: number,
  sign: string,
  signLord: string,
  signLordHouse: number,
  signLordSign: string,
  occupyingPlanets: Array<{
    name: string;
    dignity: string;
    isRetrograde: boolean;
    isCombust: boolean;
    nakshatra: string;
  }>
): DetailedHouseAnalysisResult {
  const tax = DETAILED_HOUSE_TAXONOMY[houseNumber] || DETAILED_HOUSE_TAXONOMY[1];

  // Synthesize lord placement
  let lordEn = `The lord of the ${formatOrdinal(houseNumber)} house (${signLord}) is situated in the ${formatOrdinal(signLordHouse)} house (${signLordSign}). `;
  let lordHi = `${houseNumber}वें भाव के स्वामी (${signLord}) पत्रिका के ${signLordHouse}वें भाव (${signLordSign}) में विराजमान हैं। `;

  if (signLordHouse === houseNumber) {
    lordEn += `Occupying its own house significantly strengthens this domain, granting natural autonomy, protective shielding, and auspicious results.`;
    lordHi += `स्वग्रही होने के कारण यह भाव अत्यंत सशक्त एवं संरक्षित है, जिससे इस क्षेत्र में स्वाभाविक स्थिरता और शुभ फल प्राप्त होते हैं।`;
  } else if ([1, 4, 7, 10].includes(signLordHouse)) {
    lordEn += `Its placement in a Kendra (pillar house) forms a powerful action-oriented connection, bringing active dynamism and public prominence to this life sphere.`;
    lordHi += `केंद्र (1, 4, 7, 10) भाव में स्थिति इसे कर्मठता, सामाजिक संबल और जीवन में सक्रिय प्रगति प्रदान करती है।`;
  } else if ([1, 5, 9].includes(signLordHouse)) {
    lordEn += `Its placement in a Trikona (auspicious Lakshmi sthana) infuses this house with natural divine grace, ethical intellect, and fortune.`;
    lordHi += `त्रिकोण (1, 5, 9) भाव में बैठने से इस भाव को स्वाभाविक ईश्वरीय कृपा, सद्बुद्धि और भाग्योदय का सहयोग मिलता है।`;
  } else if ([3, 6, 11].includes(signLordHouse)) {
    lordEn += `Placed in an Upachaya growth house, its strengths and rewards will steadily compound with conscious persistence, experience, and time.`;
    lordHi += `उपचय (3, 6, 11) भाव में होने से निरंतर परिश्रम व अनुभव के साथ समय के साथ इसके फल उत्तरोत्तर बढ़ते हैं।`;
  } else if ([6, 8, 12].includes(signLordHouse)) {
    lordEn += `Connected with a Trik dusthana, it invites deeper introspection, problem-solving resilience, and spiritual or transformative breakthroughs.`;
    lordHi += `त्रिक भाव (6, 8, 12) से संबंधित होने के कारण यह धैर्य, अनुशासन, गूढ़ ज्ञान और आध्यात्मिक रूपांतरण की मांग करता है।`;
  } else {
    lordEn += `This creates a steady financial and familial linkage, supporting resource management.`;
    lordHi += `यह वित्तीय व पारिवारिक संबंधों को मजबूती प्रदान करते हुए स्थिरता लाता है।`;
  }

  // Synthesize occupying planets
  let occEn = '';
  let occHi = '';
  if (occupyingPlanets.length === 0) {
    occEn = `This house is unoccupied, functioning primarily under the direct stewardship of its lord (${signLord}) and natural planetary aspects. This provides a clean, uncluttered canvas where outcomes are directly governed by your conscious choices and dasha timing.`;
    occHi = `इस भाव में कोई ग्रह प्रत्यक्ष रूप से स्थित नहीं है। यह भाव पूर्णतः अपने भावेश (${signLord}) के प्रभाव व दृष्टि संबंधों से संचालित होता है, जो इसे अनावश्यक दबाव से मुक्त रखता है।`;
  } else {
    const pNames = occupyingPlanets.map(p => `${p.name}${p.isRetrograde ? ' (R)' : ''}${p.dignity ? ` [${p.dignity}]` : ''}`).join(', ');
    occEn = `Activated by the direct presence of ${pNames}. These planetary energies directly shape your daily focus in this house, infusing it with their natural archetypes and elemental drives.`;
    occHi = `इस भाव में ${occupyingPlanets.map(p => p.name).join(', ')} की प्रत्यक्ष उपस्थिति है, जो इस जीवन क्षेत्र को ऊर्जावान बनाती है और आपके निर्णयों पर प्रत्यक्ष प्रभाव डालती है।`;
  }

  // Actionable guidance based on house classification
  let actEn = '';
  let actHi = '';
  if ([1, 5, 9].includes(houseNumber)) {
    actEn = 'Cultivate Dharmic integrity, continuous learning, and purposeful action. Align your daily decisions with your higher calling for maximum resonance.';
    actHi = 'सद्कर्म, ज्ञानार्जन और नैतिक आचरण पर बल दें। अपने जीवन मूल्यों के अनुरूप निर्णय लेने से भाग्योदय होगा।';
  } else if ([2, 6, 10].includes(houseNumber)) {
    actEn = 'Maintain consistent work discipline, structured financial planning, and professional excellence. Practical pragmatism yields durable prosperity.';
    actHi = 'वित्तीय अनुशासन, कार्यक्षेत्र में निष्ठा और व्यावहारिक दृष्टिकोण बनाए रखें। नियमित प्रयास से स्थायी समृद्धि मिलेगी।';
  } else if ([3, 7, 11].includes(houseNumber)) {
    actEn = 'Nurture high-trust partnerships, healthy communication networks, and clear collaborative goals to manifest aspirations.';
    actHi = 'पारस्परिक विश्वास, मधुर संबंध और स्पष्ट संवाद को प्राथमिकता दें। सहयोग से मनोकामनाओं की पूर्ति होगी।';
  } else {
    actEn = 'Embrace contemplative habits, stress-management routines, and selfless service to transform challenges into spiritual wisdom.';
    actHi = 'ध्यान, परोपकार और मानसिक शांति को जीवन का हिस्सा बनाएं। आंतरिक संतुलन से समस्त बाधाएं दूर होंगी।';
  }

  return {
    houseNumber,
    taxonomy: tax,
    sign,
    signLord,
    signLordHouse,
    signLordSign,
    occupyingPlanets,
    synthesisEn: `${tax.governingThemesEn} ${occEn}`,
    synthesisHi: `${tax.governingThemesHi} ${occHi}`,
    lordPlacementAnalysisEn: lordEn,
    lordPlacementAnalysisHi: lordHi,
    actionGuidanceEn: actEn,
    actionGuidanceHi: actHi
  };
}
